import type {
  ChangeEvent,
  FC,
  FocusEvent,
  HTMLInputAutoCompleteAttribute,
} from 'react';
import type { UseFormReturn } from 'react-hook-form';
import Input from '../Input/Input';
import { type IconName } from '../VectorGraphic/VectorGraphic';
import FloatingLabelWrapper from './FloatingLabelWrapper';
import {
  FormAdditionalText,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from './Form';

type GenericInputProps = {
  form: UseFormReturn<any>;
  name: string;
  label: string;
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'date';
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  icon?: IconName;
  min?: number;
  max?: number;
  maxLength?: number;
  pattern?: string;
  inputMode?:
    | 'none'
    | 'text'
    | 'tel'
    | 'url'
    | 'email'
    | 'numeric'
    | 'decimal'
    | 'search';
  autoComplete?: HTMLInputAutoCompleteAttribute;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => boolean | void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  additionalText?: string;
  dataTestId?: string;
};

export const GenericInput: FC<GenericInputProps> = ({
  form,
  name,
  label,
  icon,
  type = 'text',
  required = false,
  disabled = false,
  min,
  max,
  maxLength,
  pattern,
  inputMode,
  autoComplete,
  onChange,
  onBlur,
  additionalText,
  dataTestId,
}) => (
  <FormField
    control={form.control}
    name={name}
    render={({ field }) => {
      const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (onChange) {
          const result = onChange(e);
          if (result === false) return;
        }
        field.onChange(e);
      };

      const handleBlurWithCallback = (e: FocusEvent<HTMLInputElement>) => {
        if (onBlur) {
          onBlur(e);
        }
        field.onBlur();
      };

      return (
        <FormItem>
          <FormControl>
            <FloatingLabelWrapper
              fieldValue={field.value}
              label={label}
              required={required}
              disabled={disabled}
            >
              {({ handleFocus, handleBlur, id }) => (
                <Input
                  id={id}
                  type={type}
                  disabled={disabled}
                  icon={icon}
                  min={min}
                  max={max}
                  maxLength={maxLength}
                  pattern={pattern}
                  inputMode={inputMode}
                  autoComplete={autoComplete}
                  testid={dataTestId}
                  {...field}
                  onChange={handleChange}
                  onFocus={handleFocus}
                  onBlur={e => {
                    handleBlur();
                    handleBlurWithCallback(e);
                  }}
                />
              )}
            </FloatingLabelWrapper>
          </FormControl>

          <FormMessage />

          {additionalText && (
            <FormAdditionalText>{additionalText}</FormAdditionalText>
          )}
        </FormItem>
      );
    }}
  />
);

export default GenericInput;
